import type { ExpValue, ResourceValue } from '@/domain/types'
import { normalizeJobName } from '@/core/job-name'
import { parseMapNameText } from './map-name'

export { parseMesos } from '../mesos'

function digits(value: string): number | null {
  const normalized = value.replace(/[Oo]/g, '0').replace(/[Il|]/g, '1').replace(/[^0-9-]/g, '')
  if (!normalized || normalized === '-') return null
  const parsed = Number(normalized)
  return Number.isSafeInteger(parsed) ? parsed : null
}

function normalizedDigitText(value: string): string {
  return value.replace(/[Oo]/g, '0').replace(/[Il|]/g, '1').replace(/\D/g, '')
}

interface ParsedExpPercentage {
  readonly percentage: number
  readonly precision: number
}

function percentageFromParts(integerPart: string, fractionPart = ''): ParsedExpPercentage | null {
  const integerDigits = normalizedDigitText(integerPart)
  const fractionDigits = normalizedDigitText(fractionPart)
  if (!integerDigits) return null
  const percentage = Number(fractionDigits ? `${integerDigits}.${fractionDigits}` : integerDigits)
  return Number.isFinite(percentage) && percentage >= 0 && percentage <= 100
    ? { percentage, precision: fractionDigits.length }
    : null
}

function parseCompletePercentageBody(body: string): ParsedExpPercentage | null {
  const match = body.match(/^\s*([\dOoIl|]{1,3})(?:(?:\s*[.,．。·•:：;；'’/\\_–—-]\s*|\s+)([\dOoIl|]{1,2}))?\s*$/)
  if (!match) return null
  return percentageFromParts(match[1] ?? '', match[2] ?? '')
}

export interface ParsedExpText {
  readonly value: ExpValue | null
  readonly percentage: ParsedExpPercentage | null
  readonly ambiguous: boolean
}

/** Find field boundaries before converting OCR lookalikes into digits. */
export function parseExpText(text: string): ParsedExpText {
  const normalized = text.normalize('NFKC').trim()
  const marker = /^EXP\s*/i.test(normalized)
  const body = normalized.replace(/^EXP\s*\.?\s*/i, '')
  const percentAt = body.indexOf('%')
  const beforePercent = percentAt < 0 ? body : body.slice(0, percentAt)
  const bracketAt = beforePercent.search(/[[(【]/)
  const absoluteNumber = (part: string): number | null => {
    const token = part.trim()
    return /^(?:[\dOoIl|]+|[\dOoIl|]{1,3}(?:,[\dOoIl|]{3})+)$/.test(token) ? digits(token) : null
  }
  let absolute: number | null = null
  let percentage: ParsedExpPercentage | null = null
  let ambiguous = false
  if (bracketAt >= 0) {
    const prefix = beforePercent.slice(0, bracketAt)
    absolute = absoluteNumber(prefix)
    percentage = percentAt < 0 ? null : parseCompletePercentageBody(beforePercent.slice(bracketAt + 1))
    ambiguous = prefix.trim().length > 0 && absolute === null
  } else if (percentAt >= 0) {
    // A vertical stroke can be either a digit or a damaged opening bracket.
    // Never glue it and the percentage onto the absolute EXP field.
    const strokeAt = beforePercent.indexOf('|')
    if (strokeAt >= 0) {
      percentage = parseCompletePercentageBody(beforePercent.slice(strokeAt + 1))
      ambiguous = true
    } else {
      const separated = marker ? beforePercent.match(/^([\dOoIl,]+)\s+(.+)$/) : null
      if (separated) {
        absolute = absoluteNumber(separated[1]!)
        percentage = parseCompletePercentageBody(separated[2]!)
        ambiguous = absolute === null || percentage === null
        if (ambiguous) absolute = null
      } else {
        percentage = parseCompletePercentageBody(beforePercent)
        ambiguous = marker
      }
    }
  } else if (marker) {
    absolute = absoluteNumber(body)
    ambiguous = absolute === null
  }
  if (percentAt >= 0 && !/^%\s*[\])】]?\s*$/.test(body.slice(percentAt))) {
    absolute = null
    percentage = null
    ambiguous = true
  }
  return { value: absolute === null && percentage === null ? null
    : { absolute, ratio: percentage === null ? null : percentage.percentage / 100 }, percentage, ambiguous }
}

export function parseExpPercentage(text: string): ParsedExpPercentage | null {
  return parseExpText(text).percentage
}

export function parseLevel(text: string): number | null {
  const normalized = text.normalize('NFKC').trim()
  const markers = [...normalized.matchAll(/L\s*V\s*\.?\s*((?:[\dOoIl|]\s*){1,3})/gi)]
  if (markers.length) {
    if (markers.length !== 1) return null
    const marker = markers[0]
    const level = parseIsolatedLevelDigits(marker?.[1] ?? '')
    if (level === null) return null
    const markerStart = marker?.index ?? 0
    const markerEnd = markerStart + (marker?.[0].length ?? 0)
    const outside = `${normalized.slice(0, markerStart)} ${normalized.slice(markerEnd)}`
    // Nonnumeric labels around an explicit LV marker are harmless, but any
    // second number makes the result ambiguous and must still be rejected.
    if (/\d/.test(outside)) return null
    return level
  }

  const isolated = parseIsolatedLevelDigits(normalized)
  if (isolated !== null) return isolated

  const match = normalized.match(/\d{1,3}/)
  return validLevel(digits(match?.[0] ?? normalized))
}

function parseIsolatedLevelDigits(value: string): number | null {
  if (!/^(?:[\dOoIl|]\s*){1,3}$/.test(value)) return null
  return validLevel(digits(value))
}

function validLevel(value: number | null): number | null {
  return value !== null && value >= 1 && value <= 200 ? value : null
}

export function parseResource(text: string): ResourceValue | null {
  const match = text.match(/(?:^|[^0-9A-Za-z])([\dOoIl|,]+)\s*[\/]\s*([\dOoIl|,]+)(?![0-9A-Za-z])/)
  if (!match) return null
  const current = digits(match[1] ?? '')
  const max = digits(match[2] ?? '')
  if (current === null || max === null || max <= 0 || current > max * 1.05) return null
  return { current, max, ratio: Math.min(1, current / max) }
}

export function parseExp(text: string): ExpValue | null {
  return parseExpText(text).value
}

export function parseMapName(text: string): string | null {
  return parseMapNameText(text)
}

export function parseJobName(text: string): string | null {
  return normalizeJobName(text)
}
